import { Extension } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import { selectedRect, cellAround, TableMap } from '@tiptap/pm/tables';
import { Table, TableView } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';

const styleAttr = (name, css, parse) => ({
  default: null,
  parseHTML: (el) => parse(el) || null,
  renderHTML: (a) => (a[name] ? { style: `${css}:${a[name]}` } : {}),
});

// Adds inline-style attributes to table cells. TipTap's mergeAttributes concatenates
// duplicate `style` keys, so several style-producing attributes co-exist safely.
const withCellStyles = (Base) => Base.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      backgroundColor: styleAttr('backgroundColor', 'background-color', (el) => el.style.backgroundColor),
      borderColor: { default: null, parseHTML: (el) => el.style.borderColor || null, renderHTML: (a) => (a.borderColor ? { style: `border:1px solid ${a.borderColor}` } : {}) },
      cellWidth: styleAttr('cellWidth', 'width', (el) => el.style.width),
      cellHeight: styleAttr('cellHeight', 'height', (el) => el.style.height),
      vAlign: styleAttr('vAlign', 'vertical-align', (el) => el.style.verticalAlign),
    };
  },
});

export const StyledTableCell = withCellStyles(TableCell);
export const StyledTableHeader = withCellStyles(TableHeader);

// TableRow that persists a data-repeat attribute so a row can be marked to repeat
// per dataset at render/merge time on the backend.
export const RepeatableTableRow = TableRow.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      'data-repeat': {
        default: null,
        parseHTML: (el) => el.getAttribute('data-repeat'),
        renderHTML: (attrs) => (attrs['data-repeat'] ? { 'data-repeat': attrs['data-repeat'] } : {}),
      },
    };
  },
});

// Table with a sizing mode: null (full width), 'contents' (shrink to fit), 'equal' (equal columns).
// The node view mirrors the attribute onto the live <table> so the editor reflects it immediately.
export class LayoutTableView extends TableView {
  constructor(node, cellMinWidth, view, attrs) {
    super(node, cellMinWidth, view, attrs);
    this.applyLayout(node);
  }
  applyLayout(node) {
    if (node.attrs.layout) this.table.setAttribute('data-layout', node.attrs.layout);
    else this.table.removeAttribute('data-layout');
  }
  update(node) {
    const ok = super.update(node);
    if (ok) this.applyLayout(node);
    return ok;
  }
}

export const LayoutTable = Table.extend({
  addOptions() { return { ...this.parent?.(), View: LayoutTableView }; },
  addAttributes() {
    return {
      ...this.parent?.(),
      layout: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-layout') || null,
        renderHTML: (a) => (a.layout ? { 'data-layout': a.layout } : {}),
      },
    };
  },
});

const isCell = (node) => ['cell', 'header_cell'].includes(node?.type?.spec?.tableRole);

const nearBottom = (el, y) => { const r = el.getBoundingClientRect(); return r.bottom - y <= 5 && r.bottom - y >= -2; };

// Positions of every cell in the row the pointer is over (bottom row of a row-spanning cell).
function rowCellPositions(view, e) {
  const coords = view.posAtCoords({ left: e.clientX, top: e.clientY });
  const $cell = coords && cellAround(view.state.doc.resolve(coords.pos));
  if (!$cell) return null;
  const tableStart = $cell.start(-1);
  const map = TableMap.get($cell.node(-1));
  const row = map.findCell($cell.pos - tableStart).bottom - 1;
  return [...new Set(Array.from({ length: map.width }, (_, col) => tableStart + map.map[row * map.width + col]))];
}

// Drag the bottom edge of any cell to set the height of its whole row.
export const RowResize = Extension.create({
  name: 'rowResize',
  addProseMirrorPlugins() {
    let dragging = false;
    return [new Plugin({
      props: {
        handleDOMEvents: {
          mousemove(view, e) {
            if (dragging) return false;
            const cell = e.target.closest?.('td,th');
            view.dom.classList.toggle('row-resize-cursor', !!cell && view.dom.contains(cell) && nearBottom(cell, e.clientY));
            return false;
          },
          mousedown(view, e) {
            const cell = e.target.closest?.('td,th');
            if (!cell || !view.dom.contains(cell) || !nearBottom(cell, e.clientY)) return false;
            const positions = rowCellPositions(view, e);
            if (!positions) return false;
            e.preventDefault();
            dragging = true;
            const startY = e.clientY; const startH = cell.parentElement.getBoundingClientRect().height;
            const apply = (h) => {
              const tr = view.state.tr;
              positions.forEach((pos) => { const node = view.state.doc.nodeAt(pos); if (node) tr.setNodeMarkup(pos, null, { ...node.attrs, cellHeight: `${h}px` }); });
              view.dispatch(tr);
            };
            let raf = null;
            const move = (ev) => { const h = Math.max(20, Math.round(startH + ev.clientY - startY)); if (raf) cancelAnimationFrame(raf); raf = requestAnimationFrame(() => apply(h)); };
            const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); dragging = false; };
            window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
            return true;
          },
        },
      },
    })];
  },
});

// ---- Helpers operating on the current table selection ----

function updateCells(editor, positions, fn) {
  const { state, view } = editor;
  const tr = state.tr;
  new Set(positions).forEach((pos) => { const node = state.doc.nodeAt(pos); if (node) tr.setNodeMarkup(pos, null, fn(node.attrs, pos)); });
  view.dispatch(tr);
}

function rectOrNull(state) { try { return selectedRect(state); } catch { return null; } }

export function setRowHeight(editor, px) {
  const r = rectOrNull(editor.state); if (!r) return;
  const positions = [];
  for (let row = r.top; row < r.bottom; row += 1) for (let col = 0; col < r.map.width; col += 1) positions.push(r.tableStart + r.map.map[row * r.map.width + col]);
  updateCells(editor, positions, (attrs) => ({ ...attrs, cellHeight: px ? `${px}px` : null }));
}

export function setColumnWidth(editor, px) {
  const r = rectOrNull(editor.state); if (!r) return;
  const positions = [];
  for (let col = r.left; col < r.right; col += 1) for (let row = 0; row < r.map.height; row += 1) positions.push(r.tableStart + r.map.map[row * r.map.width + col]);
  updateCells(editor, positions, (attrs, pos) => {
    const span = attrs.colspan || 1;
    const start = r.map.colCount(pos - r.tableStart);
    const widths = Array.from({ length: span }, (_, i) => (attrs.colwidth && attrs.colwidth[i]) || null);
    for (let i = 0; i < span; i += 1) if (start + i >= r.left && start + i < r.right) widths[i] = px || null;
    const colwidth = widths.some(Boolean) ? widths.map((w) => w || 0) : null;
    return { ...attrs, colwidth, cellWidth: px && span === 1 ? `${px}px` : null };
  });
}

// mode: null = full width (auto columns), 'contents' = shrink to fit text, 'equal' = equal columns.
export function fitTable(editor, mode) {
  const r = rectOrNull(editor.state); if (!r) return;
  const tablePos = r.tableStart - 1;
  const { state, view } = editor;
  const tr = state.tr;
  tr.setNodeMarkup(tablePos, null, { ...r.table.attrs, layout: mode });
  r.table.descendants((node, pos) => {
    if (isCell(node)) tr.setNodeMarkup(r.tableStart + pos, null, { ...node.attrs, colwidth: null, cellWidth: null, cellHeight: null });
    return !isCell(node);
  });
  view.dispatch(tr);
}

export function tableLayout(editor) { const r = rectOrNull(editor.state); return r ? r.table.attrs.layout || null : null; }

// Build a TipTap table JSON document from rows of strings.
export function rowsToTable(rows, withHeader) {
  const cols = Math.max(...rows.map((r) => r.length), 1);
  const cell = (text, header) => ({ type: header ? 'tableHeader' : 'tableCell', content: [{ type: 'paragraph', content: text ? [{ type: 'text', text }] : [] }] });
  return {
    type: 'table',
    content: rows.map((r, i) => ({ type: 'tableRow', content: Array.from({ length: cols }, (_, c) => cell(r[c] || '', withHeader && i === 0)) })),
  };
}

export const SEPARATORS = [
  { key: 'tab', label: 'Tab', split: (l) => l.split('\t') },
  { key: 'comma', label: 'Comma ,', split: (l) => l.split(',') },
  { key: 'bar', label: 'Bar |', split: (l) => l.split('|') },
  { key: 'semicolon', label: 'Semicolon ;', split: (l) => l.split(';') },
  { key: 'spaces', label: '2+ spaces', split: (l) => l.split(/\s{2,}/) },
  { key: 'custom', label: 'Custom…', split: null },
];

export function parseRows(text, sepKey, custom = '', trim = true) {
  const sep = SEPARATORS.find((s) => s.key === sepKey);
  const split = sep?.split || ((l) => (custom ? l.split(custom) : [l]));
  return text.split(/\r?\n/).filter((l) => l.trim() !== '').map((l) => split(l).map((c) => (trim ? c.trim() : c)));
}
